// pastCanvasList — 過去キャンバス一覧 UI の pure ヘルパ
// (past-canvas-new-document-2026-07-05.md §7 Slice 2 / Slice 3)。
//
// DOM / fetch に触れない純関数のみ (= vitest node で全分岐テスト可能)。日時整形は
// timelapseShare.ts の `formatTimelapseTimeLabel` と同流儀 (ロケール非依存の手組み /
// ユーザのローカル時刻) で、一覧ラベルに使う「全消し日時」を出す。

import { hasPerm, Perm, type WhiteboardRestoreRejected } from '@oekaki/protocol';

const pad2 = (n: number): string => (n < 10 ? `0${n}` : `${n}`);

/**
 * ISO8601 (dispatcher の `clearedAt` / `createdAt`) → 一覧の日時ラベル
 * (`2026/07/09 18:23` 形式、ユーザのローカル時刻)。パース不能な文字列は空文字に倒す
 * (= ラベルが「全消し」だけになる。UI を壊さない)。
 */
export const formatPastCanvasDateTime = (iso: string): string => {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  const d = new Date(ms);
  return `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())} ${pad2(
    d.getHours(),
  )}:${pad2(d.getMinutes())}`;
};

/**
 * 一覧アイテムの主ラベル (`2026/07/09 18:23 全消し`)。過去キャンバスは「その時刻の
 * 全消しで確定した絵」なので `clearedAt` を主軸にする。日時が読めないときは「全消し」
 * だけ返す。
 */
export const formatPastCanvasClearedLabel = (clearedAtIso: string): string => {
  const dt = formatPastCanvasDateTime(clearedAtIso);
  return dt.length > 0 ? `${dt} 全消し` : '全消し';
};

/**
 * 「続きを描く」(= `whiteboard.restore-request`) の UI ゲート (Slice 3 §4.2)。
 *
 * `Perm.CLEAR` 保有者のみ。**`room.clearOpen` バイパスは意図的に適用しない**
 * (`canClearWhiteboard` との差分): clearOpen は「誰でもまっさらから描き始めて
 * よい」という前向きの開放であり、部屋の履歴を遡って全員のキャンバスを書き換える
 * 復元には及ばない。server (`WhiteboardRestoreService`) が同じ条件で再検証する
 * ので UI 側の表示と真の判定は冗長 (漏れがあっても server で止まる)。
 */
export const canRestorePastCanvas = (permissions: number): boolean =>
  hasPerm(permissions, Perm.CLEAR);

/**
 * `whiteboard.restore-rejected.reason` → ユーザ向け日本語メッセージ
 * (Slice 3)。imagepasteReject.ts の reason→文言マップと同流儀で application
 * 層に集約する (= モーダルは表示するだけ)。
 */
const RESTORE_REJECTED_MESSAGES: Record<WhiteboardRestoreRejected['reason'], string> = {
  // Perm.CLEAR 非保有 (clearOpen バイパス不可、§4.2)。一覧自体がオーナー限定
  // なので通常到達しないが、途中で権限を失った場合等の保険。
  'no-permission': '復元の権限がありません。オーナーとしてログインしてください',
  // pastCanvasId が当該 room の索引に無い (別部屋の id / 削除済み)。
  'not-found': 'この過去キャンバスが見つかりませんでした',
  // 索引行に snapshot が無い / R2 から種 snapshot が消えている (purge 済等)。
  'snapshot-missing': '復元できる絵が残っていませんでした',
  // 同一 room で別の復元が進行中 (多重復元ガード)。
  busy: '別の復元が進行中です。しばらく待ってからもう一度お試しください',
  // R2 種コピー等サーバ内部の失敗。
  'restore-failed': '復元に失敗しました。しばらくしてからもう一度お試しください',
};

export const restoreRejectedText = (reason: WhiteboardRestoreRejected['reason']): string =>
  RESTORE_REJECTED_MESSAGES[reason];
